import {
  Mail, Phone, MapPin, Calendar, Globe, Briefcase,
  Building2, Users2, Hash, Clock, FileText, Landmark, AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { EmployeeProfile } from "@/data/employees";
import { calcTenure } from "@/data/employees";
import { apiGetEmploymentDetails } from "@/lib/api";
import { getContractTypeLabel, isContractorContractType, isPartTimeContractType } from "@/lib/employment-contract-types";

interface ProfileAboutTabProps {
  employee: EmployeeProfile;
  isPublicOnly?: boolean;
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[minmax(120px,40%)_1fr] items-center gap-3 py-2 px-2 rounded-lg hover:bg-muted/50 transition-colors group -mx-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground group-hover:text-muted-foreground/80">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <p className="text-sm font-medium text-foreground truncate">{value}</p>
    </div>
  );
}

function SectionBlock({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border/50 bg-muted/30">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </span>
      </div>
      <div className="px-5 py-3 space-y-0.5">{children}</div>
    </div>
  );
}

export function ProfileAboutTab({ employee, isPublicOnly = false }: ProfileAboutTabProps) {
  const { t, i18n } = useTranslation();
  const tenureRaw = calcTenure(employee.startDate);
  const tenure = tenureRaw === "< 1m" ? t("profile.lessThanMonth") : tenureRaw;

  const { data: empDetails } = useQuery({
    queryKey: ["employment-details", employee.id],
    queryFn: () => apiGetEmploymentDetails(employee.id),
    enabled: !isPublicOnly,
  });
  const startDateFmt = employee.startDate
    ? `${new Date(employee.startDate).toLocaleDateString(i18n.language === "mk" ? "mk-MK" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })} (${tenure})`
    : "";

  return (
    <div className={`grid grid-cols-1 ${isPublicOnly ? "" : "lg:grid-cols-2"} gap-4`}>
      {/* Work & Contact */}
      <SectionBlock title={t("profile.sections.publicProfile")} icon={Mail}>
        <InfoRow icon={Mail} label={t("profile.emailLabel")} value={employee.email} />
        <InfoRow icon={Phone} label={t("profile.phoneLabel")} value={employee.phone} />
        <InfoRow icon={Briefcase} label={t("profile.jobTitleLabel")} value={employee.jobTitle} />
        <InfoRow icon={Building2} label={t("profile.departmentLabel")} value={employee.department} />
        <InfoRow icon={Users2} label={t("profile.teamLabel")} value={employee.team} />
        <InfoRow icon={MapPin} label={t("profile.locationLabel")} value={employee.location} />
        <InfoRow icon={Landmark} label={t("profile.companyLabel")} value={employee.company} />
        {employee.reportsTo && (
          <InfoRow icon={Users2} label={t("employees.reportsTo")} value={employee.reportsTo.name} />
        )}
      </SectionBlock>

      {/* Employment Details */}
      {!isPublicOnly && (
        <SectionBlock title={t("profile.employmentDetails")} icon={Briefcase}>
          <InfoRow icon={Hash} label={t("profile.employeeIdLabel")} value={employee.employeeId} />
          <InfoRow icon={FileText} label={t("profile.contractTypeLabel")} value={getContractTypeLabel(employee.contractType, t)} />
          <InfoRow icon={Clock} label={t("profile.workScheduleLabel")} value={employee.workSchedule} />
          <InfoRow icon={Calendar} label={t("profile.startDateLabel")} value={startDateFmt} />
          <InfoRow icon={Calendar} label={t("profile.birthdayLabel")} value={employee.birthday} />
          <InfoRow icon={Globe} label={t("profile.nationalityLabel")} value={employee.nationality} />
          <InfoRow icon={MapPin} label={t("profile.addressLabel")} value={employee.address} />
          {/* Employment details enrichment */}
          {empDetails?.workArrangement && (
            <InfoRow icon={MapPin} label={t("profile.workArrangement")} value={t(`profile.workArrangements.${empDetails.workArrangement}`, empDetails.workArrangement)} />
          )}
          {empDetails?.ftePercentage != null && isPartTimeContractType(employee.contractType) && (
            <InfoRow icon={Users2} label={t("profile.ftePercentage")} value={`${empDetails.ftePercentage}%`} />
          )}
          {empDetails?.contractorCompany && isContractorContractType(employee.contractType) && (
            <InfoRow icon={Building2} label={t("profile.contractorCompany")} value={empDetails.contractorCompany} />
          )}
          {empDetails?.internshipType && employee.contractType === "Intern" && (
            <InfoRow icon={Briefcase} label={t("profile.internshipType")} value={empDetails.internshipType === "paid" ? t("profile.paid") : t("profile.unpaid")} />
          )}
          {empDetails?.contractEndDate && (() => {
            const days = Math.ceil((new Date(empDetails.contractEndDate!).getTime() - Date.now()) / 86400000);
            const label = employee.contractType === "Intern" ? t("profile.internshipEndDate") : t("profile.contractEndDate");
            const val = `${empDetails.contractEndDate}${days >= 0 ? ` (${t("profile.daysRemaining", { count: days })})` : ` (${t("profile.expired")})`}`;
            const isUrgent = days >= 0 && days <= 30;
            const isExpired = days < 0;
            return (
              <div className={`grid grid-cols-[minmax(120px,40%)_1fr] items-center gap-3 py-2 px-2 rounded-lg -mx-2 ${isExpired ? "bg-red-50" : isUrgent && days <= 7 ? "bg-red-50" : isUrgent ? "bg-amber-50" : ""}`}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {isUrgent || isExpired ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" /> : <Calendar className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">{label}</span>
                </div>
                <p className={`text-sm font-medium truncate ${isExpired || (isUrgent && days <= 7) ? "text-red-600" : isUrgent ? "text-amber-600" : "text-foreground"}`}>{val}</p>
              </div>
            );
          })()}
        </SectionBlock>
      )}
    </div>
  );
}
